// per specs/060-agreed-alerts/plan.md C5 (kinds, params and periodicity of A4, A5, A9, A10, A12)
// The parameters of the five agreed rule kinds as the rule form holds them: typed text in, the
// zod-shaped params out. Kept apart from the form so the ranges are testable without a DOM; the
// server validates every one of them again on save.

export const AGREED_ALERT_KINDS = [
  'watched_zero_stock',
  'watched_depletion_risk',
  'sudden_drop',
  'watched_export',
  'reporting_coverage',
] as const;

export type AgreedAlertKind = (typeof AGREED_ALERT_KINDS)[number];

/** A4, A5 and A10 evaluate the members of one watched group. */
export const GROUP_KINDS: readonly AgreedAlertKind[] = [
  'watched_zero_stock',
  'watched_depletion_risk',
  'watched_export',
];

export function isAgreedKind(kind: string): kind is AgreedAlertKind {
  return (AGREED_ALERT_KINDS as readonly string[]).includes(kind);
}

export interface AgreedKindForm {
  groupId: string;
  dropPercent: string;
  windowHours: string;
  windowMinutes: string;
  minPercent: string;
}

// The defaults of C5: A9 at 50 % in 48 h, A10 over the last 60 minutes, A12 at 80 % over 48 h.
export const AGREED_KIND_DEFAULTS: AgreedKindForm = {
  groupId: '',
  dropPercent: '50',
  windowHours: '48',
  windowMinutes: '60',
  minPercent: '80',
};

type NumberField = 'dropPercent' | 'windowHours' | 'windowMinutes' | 'minPercent';

const RANGES: Record<NumberField, { min: number; max: number }> = {
  dropPercent: { min: 1, max: 100 },
  windowHours: { min: 1, max: 168 },
  windowMinutes: { min: 15, max: 1440 },
  minPercent: { min: 1, max: 100 },
};

/** The bounds of a numeric field, for the sentence that names them. */
export function agreedRange(field: string): { min: number; max: number } {
  return RANGES[field as NumberField] ?? { min: 0, max: 0 };
}

const FIELDS_OF: Record<AgreedAlertKind, readonly NumberField[]> = {
  watched_zero_stock: [],
  watched_depletion_risk: [],
  sudden_drop: ['dropPercent', 'windowHours'],
  watched_export: ['windowMinutes'],
  reporting_coverage: ['minPercent', 'windowHours'],
};

export function agreedKindFromParams(
  kind: string,
  params: Record<string, unknown>,
): AgreedKindForm {
  const read = (key: keyof AgreedKindForm): string => {
    const value = params[key];
    return value === undefined || value === null ? AGREED_KIND_DEFAULTS[key] : String(value);
  };
  if (!isAgreedKind(kind)) return { ...AGREED_KIND_DEFAULTS };
  return {
    groupId: read('groupId'),
    dropPercent: read('dropPercent'),
    windowHours: read('windowHours'),
    windowMinutes: read('windowMinutes'),
    minPercent: read('minPercent'),
  };
}

export function agreedKindToParams(
  kind: AgreedAlertKind,
  form: AgreedKindForm,
): Record<string, unknown> {
  switch (kind) {
    case 'watched_zero_stock':
    case 'watched_depletion_risk':
      return { groupId: form.groupId };
    case 'sudden_drop':
      return { dropPercent: Number(form.dropPercent), windowHours: Number(form.windowHours) };
    case 'watched_export':
      return { groupId: form.groupId, windowMinutes: Number(form.windowMinutes) };
    case 'reporting_coverage':
      return {
        minPercent: Number(form.minPercent),
        windowHours: Number(form.windowHours),
        scope: 'NATIONAL_AND_REGION',
      };
  }
}

/** Field name to the translation key of its refusal; empty when the kind's params are in range. */
export function agreedKindErrors(
  kind: AgreedAlertKind,
  form: AgreedKindForm,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (GROUP_KINDS.includes(kind) && form.groupId === '') errors['groupId'] = 'form.field.required';
  for (const field of FIELDS_OF[kind]) {
    const raw = form[field].trim();
    if (raw === '') {
      errors[field] = 'form.field.required';
      continue;
    }
    const value = Number(raw);
    const { min, max } = RANGES[field];
    if (!Number.isInteger(value) || value < min || value > max) {
      errors[field] = 'watched_groups.rule.range';
    }
  }
  return errors;
}

/** The periodicity C5 gives a new rule of the kind: hourly for A4 and A10, daily for the rest. */
export function defaultEvalMinutes(kind: string): number {
  if (kind === 'watched_zero_stock' || kind === 'watched_export') return 60;
  if (isAgreedKind(kind)) return 1440;
  return 60;
}
